﻿import type { Maybe } from '@/types'
import { msalInstance, tokenRequest } from '@/utils/msal'
import type {
    AccountInfo,
    NavigationClient,
    RedirectRequest
} from '@azure/msal-browser'
import { debug } from 'console'
import { defineStore } from 'pinia'
import { watch, computed, ref, type ComputedRef, type Ref } from 'vue'

function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}

export type UseAuthenticationStore = {
    account: Ref<Maybe<AccountInfo>>
    login: (request?: RedirectRequest) => Promise<void>
    logout: () => Promise<void>
    getToken: () => Promise<string | null>
    initialize: (client?: NavigationClient) => Promise<void>
    clearNewUser: () => void
    isNewUser: ComputedRef<boolean>
    isLoggedIn: ComputedRef<boolean>
    userId: ComputedRef<Maybe<string>>
    userFirstName: ComputedRef<string>
    userLastname: ComputedRef<string>
    userEmail: ComputedRef<Maybe<string>>
}
export const useAuthenticationStore = defineStore(
    'authentication',
    function (): UseAuthenticationStore {
        const account = ref<Maybe<AccountInfo>>(null)
        const initialized = ref(false)
        async function handleRedirect(): Promise<void> {
            const result = await msalInstance.handleRedirectPromise()
            if (result) {
                if (result.account) setAccount(result.account)
                return
            }
        }
        async function initialize(client?: NavigationClient): Promise<void> {
            console.debug('initializing auth store')
            if (initialized.value) return
            await handleRedirect()
            if (client) {
                msalInstance.setNavigationClient(client)
            }
            const accounts = msalInstance.getAllAccounts()
            if (accounts.length > 0) {
                setAccount(accounts[0])
            }
            initialized.value = true
        }
        function setAccount(newAccount: AccountInfo) {
            msalInstance.setActiveAccount(newAccount)
            // hack to get the user id from the id token
            if (newAccount.idTokenClaims?.idp === 'facebook.com') {

            }

            account.value = newAccount
        }

        function setUserId(userId: string) {
            var accountInfo = account.value
            if (accountInfo) {
                accountInfo.localAccountId = userId
                account.value = accountInfo
            }
        }

        async function login(
            request: RedirectRequest = {
                scopes: ['openid', import.meta.env.VITE_MSAL_SCOPE as string]
            }
        ): Promise<void> {
            try {
                var result = await msalInstance.loginRedirect(request)

                return result;
            } catch (error: any) {
                // if we get stuck, clear session and attempt to log in again
                if (error.errorCode === 'interaction_in_progress') {
                    sessionStorage.clear()
                    return login(request)
                }
                throw error
            }
        }
        async function getToken(): Promise<string | null> {
            console.debug('requesting token:', tokenRequest)
            try {
                console.debug('getting token silently')
                return (await msalInstance.acquireTokenSilent(tokenRequest))
                    .accessToken
            } catch (error) {
                console.debug(
                    'Silent token acquisition failed. Using interactive mode'
                )
                await msalInstance.acquireTokenRedirect(tokenRequest)
            }
            return null
        }
        async function logout() {
            await msalInstance.logoutRedirect({
                postLogoutRedirectUri: window.location.origin
            })
            account.value = null
        }
        async function clearNewUser() {
            if (account.value?.idTokenClaims?.newUser === undefined) return
            account.value.idTokenClaims.newUser = false
        }
        const isNewUser = computed(
            () => (account.value?.idTokenClaims?.newUser as boolean) ?? false
        )
        const isLoggedIn = computed(() => {
            //    console.log(account.value)
            if (account.value) return true
            const accounts = msalInstance.getAllAccounts()
            if (accounts.length > 0) {
                setAccount(accounts[0])
                return true
            }
            return false
        })

                // Helper function to read a cookie by name
        function getCookie(name: string): string | null {
            const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return match ? decodeURIComponent(match[2]) : null;
        }
        function deleteCookie(name: string, path: string = "/", domain?: string): void {
            document.cookie = `${name}=; Path=${path};${domain ? ` Domain=${domain};` : ""} Expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
        }

        // Reactive ref to track the cookie value
        const cookieUserId = ref<string | null>(getCookie('eventUserId'));

        // Periodically update the cookie value
        function updateCookieValue() {
            cookieUserId.value = getCookie('eventUserId');
            if (cookieUserId.value)
            {
                alert(`User ID found in cookie: [eventUserId] ${cookieUserId.value}`);
                setUserId(cookieUserId.value);
            }
        }

        const userEmail = computed<Maybe<string>>(() => {
            return account.value?.idTokenClaims?.emails?.[0] ?? null;
        });

        // Computed property for userId
        const userId = computed<Maybe<string>>(() => {
            updateCookieValue(); // Initial check
            return cookieUserId.value ?? account.value?.localAccountId ?? null;
        });

        // Watch for changes in userId and show alert
        watch(cookieUserId, (newValue, oldValue) => {
            if (newValue && newValue !== oldValue) {
                alert(`User ID found in cookie: ${newValue}`);
        //        deleteCookie('userId', "/", window.location.hostname);
            }
        });

        const userFirstName = computed(
        
            () => (account.value?.idTokenClaims?.given_name as string) ?? ''
        )

        const userLastname = computed(
            () => (account.value?.idTokenClaims?.family_name as string) ?? ''
        )
        return {
            account,
            login,
            logout,
            getToken,
            initialize,
            clearNewUser,
            isNewUser,
            isLoggedIn,
            userId,
            userFirstName,
            userLastname,
            userEmail
            
        }
    }
)
